/**
 * O efeito de fundo da câmera (desfoque ou imagem) DESTE lado: a escolha, se está aplicando e o erro. Mora acima da
 * troca de janela (guia / flutuante), como o resto do estado da sala. A escolha fica lembrada no navegador e vale na
 * próxima chamada. Aplica o efeito na trilha da câmera sempre que a escolha muda, a câmera liga ou a trilha é
 * republicada; as operações andam uma de cada vez (escolher depressa vários efeitos não corre entre si).
 * Sem provider (testes de peças soltas) ou sem suporte do navegador o recurso não existe: `supported: false`.
 *
 * Fundo personalizado: a imagem que a pessoa envia (ver `customBackground`) entra como mais uma opção, `custom`, e fica só
 * neste navegador. Uma escolha lembrada que aponta para ela espera a imagem ser lida; se ela sumiu, volta a "sem efeito".
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { createBackgroundSession } from './backgroundEffect';
import {
    BACKGROUND_OPTIONS, NO_BACKGROUND, backgroundOptionOf, customBackgroundOption, readStoredBackground, storeBackground, supportsBackgroundEffects,
} from './backgrounds';
import { CUSTOM_BACKGROUND_ID, pickImageFile, UPLOAD_ERRORS } from './customBackground';
import { useCustomBackground } from './useCustomBackground';

export const BACKGROUND_ERROR = 'Não foi possível aplicar o efeito de fundo neste dispositivo. A câmera segue sem efeito.';

const UNAVAILABLE = Object.freeze({
    supported: false, options: BACKGROUND_OPTIONS, choice: NO_BACKGROUND, applying: false, error: null, hasCustom: false, uploading: false,
    select() {}, clearError() {}, uploadCustom() {}, removeCustom() {},
});

const BackgroundEffectContext = createContext(null);

/**
 * @returns {{
 *   supported: boolean, options: readonly object[], choice: string, applying: boolean, error: string|null,
 *   hasCustom: boolean, uploading: boolean,
 *   select: (id: string) => void, clearError: () => void,
 *   uploadCustom: (doc: Document) => Promise<void>, removeCustom: () => Promise<void>,
 * }}
 */
export function useBackgroundEffect() {
    return useContext(BackgroundEffectContext) ?? UNAVAILABLE;
}

/**
 * @param {{
 *   children: React.ReactNode, session?: ReturnType<typeof createBackgroundSession>,
 *   customStore?: Parameters<typeof useCustomBackground>[0]['store'],
 * }} props `session` e `customStore`: para os testes.
 */
export function BackgroundEffectProvider({ children, session: injected, customStore }) {
    const { cameraTrack, isCameraEnabled } = useLocalParticipant();
    const track = cameraTrack?.track ?? null;
    const supported = useMemo(() => supportsBackgroundEffects(), []);
    const [choice, setChoice] = useState(() => (supported ? readStoredBackground() : NO_BACKGROUND));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);
    const session = useRef(injected ?? null);
    const queue = useRef(Promise.resolve());
    const custom = useCustomBackground({ enabled: supported, store: customStore });
    const customUrl = custom.url;
    const options = useMemo(() => (customUrl ? [...BACKGROUND_OPTIONS, customBackgroundOption(customUrl)] : BACKGROUND_OPTIONS), [customUrl]);

    // A escolha lembrada era o fundo personalizado e a imagem não está mais guardada (outro navegador, dados apagados).
    useEffect(() => {
        if (custom.loaded && choice === CUSTOM_BACKGROUND_ID && !customUrl) {
            storeBackground(NO_BACKGROUND);
            setChoice(NO_BACKGROUND);
        }
    }, [custom.loaded, choice, customUrl]);

    useEffect(() => {
        // Câmera desligada: o SDK reinicia o processador ao ligar de novo, e este efeito roda outra vez.
        if (!supported || !track || !isCameraEnabled) return undefined;
        // O fundo personalizado ainda está sendo lido (ou sumiu): não há o que aplicar, e "sem efeito" tiraria o que está lá.
        if (choice === CUSTOM_BACKGROUND_ID && !customUrl) return undefined;
        const option = choice === CUSTOM_BACKGROUND_ID ? customBackgroundOption(customUrl) : backgroundOptionOf(choice);
        let current = true;
        session.current ??= createBackgroundSession();
        queue.current = queue.current.then(async () => {
            // Uma escolha mais nova já está na fila: esta não precisa rodar.
            if (!current) return;
            setApplying(choice !== NO_BACKGROUND);
            try {
                await session.current.apply(track, option);
            } catch {
                if (current) {
                    // Sem o efeito neste aparelho: volta ao normal e não tenta de novo na próxima chamada. O aviso fica até
                    // ser dispensado (ou a pessoa escolher outra vez): o "sem efeito" que vem a seguir não pode apagá-lo.
                    setError(BACKGROUND_ERROR);
                    storeBackground(NO_BACKGROUND);
                    setChoice(NO_BACKGROUND);
                }
            } finally {
                if (current) setApplying(false);
            }
        });
        return () => { current = false; };
    }, [supported, track, isCameraEnabled, choice, customUrl]);

    const select = useCallback((id) => {
        if (!supported) return;
        // O fundo personalizado só vale com a imagem guardada; qualquer id desconhecido vira "sem efeito".
        const next = id === CUSTOM_BACKGROUND_ID && customUrl ? CUSTOM_BACKGROUND_ID : backgroundOptionOf(id).id;
        setError(null);
        setChoice(next);
        storeBackground(next);
    }, [supported, customUrl]);
    const clearError = useCallback(() => setError(null), []);

    // `doc`: o documento da sala (na janela flutuante não é o da guia), onde o seletor de arquivo abre.
    const { upload, remove } = custom;
    const uploadCustom = useCallback(async (doc) => {
        if (!supported) return;
        const file = await pickImageFile(doc);
        if (!file) return;
        setError(null);
        const result = await upload(file);
        if (!result.ok) {
            setError(result.message);
            return;
        }
        // Não guardou (janela privativa): vale nesta chamada, e a pessoa sabe que não volta na próxima.
        if (!result.persisted) setError(UPLOAD_ERRORS.notSaved);
        setChoice(CUSTOM_BACKGROUND_ID);
        storeBackground(CUSTOM_BACKGROUND_ID);
    }, [supported, upload]);
    const removeCustom = useCallback(async () => {
        await remove();
        setError(null);
        // Tirar a imagem que está em uso: volta a "sem efeito" (o efeito acima limpa a trilha).
        setChoice((current) => (current === CUSTOM_BACKGROUND_ID ? NO_BACKGROUND : current));
        if (readStoredBackground() === CUSTOM_BACKGROUND_ID) storeBackground(NO_BACKGROUND);
    }, [remove]);

    const value = useMemo(
        () => ({
            supported, options, choice, applying, error, hasCustom: Boolean(customUrl), uploading: custom.uploading,
            select, clearError, uploadCustom, removeCustom,
        }),
        [supported, options, choice, applying, error, customUrl, custom.uploading, select, clearError, uploadCustom, removeCustom],
    );
    return <BackgroundEffectContext.Provider value={value}>{children}</BackgroundEffectContext.Provider>;
}
